import { useRef, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { v4 as uuidv4 } from "uuid"
import { z } from "zod"

import { isValidDomain, normalizeDomain } from "@productlane/lib/src/domain"
import { enabledAddresses } from "@productlane/lib/src/email-settings"
import { scale } from "@productlane/lib/src/plans-client"
import {
	Button,
	Dialog,
	Form,
	FormInput,
	FormSelect,
	FormSwitch,
	Label,
	SecretReveal,
	SubmitButton,
	Switch,
	Tabs,
	TabsList,
	TabsTrigger,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { PortalOverrides } from "@/screens/settings/portal-instance-form"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useRequiredPlans } from "@/lib/plans"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { useSyncedTeams } from "@/screens/app/threads/hooks/useSelectedTeams"
import {
	DEFAULT_PORTAL_OVERRIDES,
	portalHostname,
	RESERVED_SLUG_ERROR,
	RESERVED_SLUGS,
	resolveAddressId,
	resolveTeamId,
	SLUG_ERROR,
	SLUG_PATTERN,
	slugify,
	visibilityToShowInSelector,
	WORKSPACE_DEFAULT_TEAM,
} from "@/screens/settings/portal-instance-form"
import { PortalLogoField } from "@/screens/settings/portal-logo-field"
import { SettingsHeader } from "@/screens/settings/settings-header"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsSection } from "@/screens/settings/settings-switches/settings-section"
import { usePortalLogoUpload } from "@/screens/settings/use-portal-logo-upload"
import {
	PortalAuthSection,
	PortalLinksSection,
} from "./portal-override-sections"

const FormSchema = z
	.object({
		name: z.string().min(1, "Name is required").max(80),
		slug: z
			.string()
			.regex(SLUG_PATTERN, SLUG_ERROR)
			.refine((s) => !RESERVED_SLUGS.has(s), {
				message: RESERVED_SLUG_ERROR,
			}),
		defaultLinearTeamId: z.string(),
		defaultEmailAddressId: z.string(),
		visibility: z.enum(["shown", "hidden"]),
		showDocs: z.boolean(),
		showChangelog: z.boolean(),
		showRoadmap: z.boolean(),
		showSupport: z.boolean(),
		ownSso: z.boolean(),
		ssoTokenEndpoint: z.string(),
		addressMode: z.enum(["path", "domain"]),
		domain: z.string(),
	})
	.superRefine((values, ctx) => {
		if (
			values.addressMode === "domain" &&
			!isValidDomain(normalizeDomain(values.domain))
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["domain"],
				message: "Enter a bare domain like docs.example.com",
			})
		}
		if (!values.ownSso) return
		const url = values.ssoTokenEndpoint.trim()
		if (!z.string().url().safeParse(url).success) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["ssoTokenEndpoint"],
				message: "Enter a full address, like https://acme.com/api/productlane",
			})
		} else if (!url.startsWith("https://")) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["ssoTokenEndpoint"],
				message: "The token endpoint must use https.",
			})
		}
	})

const LIST_PATH = "/settings/portal/instances"

export function NewPortalPage() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const navigate = useNavigate()
	const uploadLogo = usePortalLogoUpload()
	const { passes: hasRequiredPlan } = useRequiredPlans(scale.andUp)
	const teams = useSyncedTeams()

	const [logoFile, setLogoFile] = useState<File | null>(null)
	const [logoPreview, setLogoPreview] = useState<string | null>(null)
	const [overrides, setOverrides] = useState<PortalOverrides>(
		DEFAULT_PORTAL_OVERRIDES,
	)
	const [ssoSecret, setSsoSecret] = useState<{
		portalId: string
		secret: string
	} | null>(null)
	const activateSso = api.workspace.activateSso.useMutation()
	const setPortalDomain = api.workspace.setPortalInstanceDomain.useMutation()
	// A field's blur flush and the submit click land in the same tick, so the
	// submit reads the ref instead of a closure over stale state.
	const overridesRef = useRef(overrides)
	overridesRef.current = overrides

	const updateOverrides = (args: Partial<PortalOverrides>) =>
		setOverrides((current) => ({ ...current, ...args }))

	const form = useZodForm({
		schema: FormSchema,
		defaultValues: {
			name: "",
			slug: "",
			defaultLinearTeamId: WORKSPACE_DEFAULT_TEAM,
			defaultEmailAddressId: WORKSPACE_DEFAULT_TEAM,
			visibility: "shown",
			showDocs: true,
			showChangelog: true,
			showRoadmap: true,
			showSupport: true,
			ownSso: false,
			ssoTokenEndpoint: "",
			addressMode: "path",
			domain: "",
		},
	})
	const addressMode = form.watch("addressMode")
	const ownSso = form.watch("ownSso")

	if (!workspace) return null

	if (!hasRequiredPlan) {
		void navigate({ to: LIST_PATH, replace: true })
		return null
	}

	const hostname = portalHostname(workspace)
	const addresses = enabledAddresses(workspace.emailSettings)

	const handleSubmit = async (values: z.infer<typeof FormSchema>) => {
		const slugTaken = (workspace.portalInstances ?? []).some(
			(i) => i.slug === values.slug,
		)
		if (slugTaken) {
			form.setError("slug", {
				message: `A portal already uses /${values.slug}. Pick another URL.`,
			})
			return
		}

		const id = uuidv4()
		try {
			const logoUrl = logoFile ? await uploadLogo(logoFile) : null
			const mutation = zero.mutate(
				mutators.portalInstances.create({
					id,
					slug: values.slug,
					name: values.name,
					logoUrl,
					showInSelector: visibilityToShowInSelector(values.visibility),
					showDocs: values.showDocs,
					showChangelog: values.showChangelog,
					showRoadmap: values.showRoadmap,
					showSupport: values.showSupport,
					defaultLinearTeamId: resolveTeamId(values.defaultLinearTeamId),
					defaultEmailAddressId: resolveAddressId(values.defaultEmailAddressId),
					...overridesRef.current,
				}),
			)
			await mutation.client
			if (values.addressMode === "domain") {
				await mutateOk(mutation, "server")
				await setPortalDomain
					.mutateAsync({
						portalInstanceId: id,
						domain: normalizeDomain(values.domain),
					})
					.catch((error: unknown) =>
						toast.error({
							title: "Your domain was not added",
							description:
								error instanceof Error ? error.message : "Try again.",
						}),
					)
			}
			if (values.ownSso) {
				// The SSO row points at the portal row, so the portal has to be committed first.
				await mutateOk(mutation, "server")
				await activateSsoForPortal(id, values.ssoTokenEndpoint.trim())
				return
			}
			void navigate({
				to: "/settings/portal/instances/$portalId",
				params: { portalId: id },
				replace: true,
			})
			mutation.server.catch((err) => {
				captureError(err, {
					domain: "portal",
					action: "create portal instance",
					workspaceId: workspace.id,
				})
				toast.error({
					title: "Your portal did not save",
					description:
						err instanceof Error ? err.message : "Try creating it again.",
				})
			})
		} catch (e) {
			captureError(e, {
				domain: "portal",
				action: "create portal instance",
				workspaceId: workspace.id,
			})
			toast.error({
				title: "Your portal did not save",
				description: e instanceof Error ? e.message : "Try creating it again.",
			})
		}
	}

	const activateSsoForPortal = async (
		portalId: string,
		tokenEndpoint: string,
	) => {
		try {
			const result = await activateSso.mutateAsync({
				tokenEndpoint,
				portalInstanceId: portalId,
				isEnabled: true,
			})
			setSsoSecret({ portalId, secret: result.secret })
			await navigator.clipboard.writeText(result.secret)
			toast.success({ title: "Secret copied" })
		} catch (e) {
			captureError(e, {
				domain: "portal",
				action: "activate portal sso",
				workspaceId: workspace.id,
				extra: { portalInstanceId: portalId },
			})
			toast.error({
				title: "Your portal was created, but SSO was not set up",
				description:
					e instanceof Error
						? e.message
						: "Set it up from the portal's SSO page.",
			})
			void navigate({
				to: "/settings/portal/instances/$portalId",
				params: { portalId },
				replace: true,
			})
		}
	}

	const closeSecret = () => {
		if (!ssoSecret) return
		const { portalId } = ssoSecret
		setSsoSecret(null)
		void navigate({
			to: "/settings/portal/instances/$portalId",
			params: { portalId },
			replace: true,
		})
	}

	return (
		<>
			<MetaInfo title="New portal" />

			<SettingsHeader
				title="New portal"
				subtitle={`Serves ${hostname}/your-slug with its own logo and sections.`}
			/>

			<Form form={form} onSubmit={(values) => void handleSubmit(values)}>
				<SettingsContainer className="flex flex-col gap-4 divide-y-0">
					<div className="flex flex-col">
						<Label className="mb-1.5" htmlFor="name">
							Name
						</Label>
						<div className="flex items-start gap-2">
							<PortalLogoField
								logoUrl={logoPreview}
								onPicked={(file, previewUrl) => {
									setLogoFile(file)
									setLogoPreview(previewUrl)
								}}
								onCleared={() => {
									setLogoFile(null)
									setLogoPreview(null)
								}}
							/>
							<FormInput
								className="flex-1"
								errorMessage={form.formState.errors.name?.message}
								name="name"
								placeholder="Enterprise"
								onChange={(e) => {
									form.setValue("name", e.target.value)
									if (!form.formState.dirtyFields.slug) {
										form.setValue("slug", slugify(e.target.value), {
											shouldValidate: true,
										})
									}
								}}
							/>
						</div>
					</div>

					<div className="flex flex-col gap-1.5">
						<div className="flex items-center justify-between">
							<Label htmlFor={addressMode === "path" ? "slug" : "domain"}>
								Address
							</Label>
							<Tabs
								value={addressMode}
								onValueChange={(value) =>
									form.setValue("addressMode", value as "path" | "domain")
								}
							>
								<TabsList>
									<TabsTrigger value="path">Path</TabsTrigger>
									<TabsTrigger value="domain">Own domain</TabsTrigger>
								</TabsList>
							</Tabs>
						</div>
						{addressMode === "path" ? (
							<FormInput
								errorMessage={form.formState.errors.slug?.message}
								name="slug"
								placeholder="enterprise"
								prefix={<span className="text-sm">{hostname}/</span>}
							/>
						) : (
							<FormInput
								errorMessage={form.formState.errors.domain?.message}
								name="domain"
								placeholder="docs.example.com"
							/>
						)}
					</div>

					{teams.length ? (
						<FormSelect
							name="defaultLinearTeamId"
							label="Linear team"
							options={[
								{ label: "Workspace default", value: WORKSPACE_DEFAULT_TEAM },
								...teams.map((team) => ({
									label: team.name,
									value: team.id,
								})),
							]}
							selectClassName="dark:bg-gray-700/70"
						/>
					) : null}

					{addresses.length > 1 ? (
						<FormSelect
							name="defaultEmailAddressId"
							label="Reply address"
							options={[
								{ label: "Workspace default", value: WORKSPACE_DEFAULT_TEAM },
								...addresses.map((address) => ({
									label: address.primaryEmail,
									value: address.id,
								})),
							]}
							selectClassName="dark:bg-gray-700/70"
						/>
					) : null}

					<FormSelect
						name="visibility"
						label="Portal switcher"
						options={[
							{ label: "Show the switcher", value: "shown" },
							{ label: "Hide the switcher", value: "hidden" },
						]}
						selectClassName="dark:bg-gray-700/70"
					/>
				</SettingsContainer>

				<SettingsSection
					title="Sections"
					description="Pick what this portal shows. You can change it any time."
					className="mb-3"
				/>
				<SettingsContainer className="flex flex-col gap-3 divide-y-0">
					<FormSwitch
						name="showDocs"
						label={<div className="text-primary">Docs</div>}
					/>
					<FormSwitch
						name="showChangelog"
						label={<div className="text-primary">Changelog</div>}
					/>
					<FormSwitch
						name="showRoadmap"
						label={<div className="text-primary">Feature requests</div>}
					/>
					<FormSwitch
						name="showSupport"
						label={<div className="text-primary">Support portal</div>}
					/>
				</SettingsContainer>

				<PortalAuthSection
					values={overrides}
					onSave={updateOverrides}
					gate={{
						checked: overrides.overridesAuth,
						onChange: (overridesAuth) => updateOverrides({ overridesAuth }),
					}}
				/>

				<PortalLinksSection
					values={overrides}
					onSave={updateOverrides}
					gate={{
						checked: overrides.overridesCustomLinks,
						onChange: (overridesCustomLinks) =>
							updateOverrides({ overridesCustomLinks }),
					}}
				/>

				<SettingsSection
					title="SSO"
					description="Authenticate visitors with your own identity provider."
					className="mb-3"
				/>
				<SettingsContainer className="flex flex-col gap-4 divide-y-0">
					<SettingItem
						title="Own SSO connection"
						description="Its own identity provider instead of the root portal's."
						action={
							<Switch
								name="ownSso"
								label={null}
								className="pr-1"
								checked={ownSso}
								onCheckedChange={(checked) =>
									form.setValue("ownSso", checked, { shouldValidate: true })
								}
							/>
						}
					/>
					{ownSso && (
						<FormInput
							name="ssoTokenEndpoint"
							label="Token endpoint"
							placeholder="https://example.com/api/productlane"
							errorMessage={form.formState.errors.ssoTokenEndpoint?.message}
						/>
					)}
				</SettingsContainer>

				<div className="mt-6 flex justify-end gap-2">
					<Button
						type="button"
						variant="outline"
						onClick={() => void navigate({ to: LIST_PATH })}
					>
						Cancel
					</Button>
					<SubmitButton>Create portal</SubmitButton>
				</div>
			</Form>

			<Dialog
				isOpen={!!ssoSecret}
				onOpenChange={(open) => {
					if (!open) closeSecret()
				}}
				title="Save your secret"
			>
				<SecretReveal
					value={ssoSecret?.secret ?? ""}
					description="This secret authenticates your SSO endpoint with Productlane."
					onCopied={() => toast.success({ title: "Secret copied" })}
					onClose={closeSecret}
				/>
			</Dialog>
		</>
	)
}
